import { Avatar, Divider, Link, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { DataItem } from '../DataItem';
import { APPLY_TYPES, STATUS_TYPES } from 'Enum/Applies';
import { replaceZerosWithHyphen } from 'Utils/replaceZerosWithHyphen';
import { shortDateFormat } from 'Config';
import { format } from 'date-fns';
import { AccordionBadge } from '../AccordionBadge';
import { OutlinedPhone, OutlinedScreen, WarningYellow } from 'Images';

const colorsByStatus: {
  [index: number]: { bgcolor: string; borderColor: string };
} = {
  [STATUS_TYPES.WAITING_TO_OPEN_IN_SYSTEM]: {
    bgcolor: 'purple2.shade_100',
    borderColor: 'purple2.shade_200',
  },
  [STATUS_TYPES.INFORMATION_WAS_PROVIDED]: {
    bgcolor: '#E0F6EF',
    borderColor: '#B8EAD9',
  },
  [STATUS_TYPES.FORWARDED_TO_EXPERT]: {
    bgcolor: '#FFF7EA',
    borderColor: '#FFE3B2',
  },
  [STATUS_TYPES.EXPERT_REPLY_WAS_RECEIVED]: {
    bgcolor: 'primary.shade_100',
    borderColor: 'primary.shade_200',
  },
  [STATUS_TYPES.UNKNOWN]: {
    bgcolor: 'primary.shade_100',
    borderColor: 'primary.shade_200',
  },
};

interface IApplyAccordionSummary {
  type: APPLY_TYPES;
  title?: string;
  applyNumber: string;
  statusUpdateDate: Date;
  linkSuffixText?: string;
  linkPrefixText?: string;
  linkText?: string;
  linkUrl?: string;
  statusDescription?: string;
  status: number;
  isAdminIsrId: boolean;
}

export const ApplyAccordionSummary = ({
  type,
  title,
  status,
  applyNumber,
  statusUpdateDate,
  linkPrefixText,
  linkSuffixText,
  linkText,
  linkUrl,
  statusDescription,
  isAdminIsrId,
}: IApplyAccordionSummary) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Applies);
  const isDesktop = useIsDesktop();

  const getApplyNumber = () =>
    applyNumber
      ? replaceZerosWithHyphen(applyNumber)
      : dictionary.toBeDetermined;

  const getStatusUpdateDate = () =>
    !!statusUpdateDate
      ? format(new Date(statusUpdateDate), shortDateFormat)
      : undefined;

  const getStatusContent = () => {
    if (status === STATUS_TYPES.UNKNOWN && linkUrl) {
      return (
        <AccordionBadge
          bgcolor={colorsByStatus[status].bgcolor}
          borderColor={colorsByStatus[status].borderColor}
        >
          <Stack>
            <Typography variant={'body5'} tabIndex={0}>
              {linkPrefixText}
              {' > '}
            </Typography>
            <Link href={linkUrl}>
              <Typography variant={'body3'}>
                {linkText}
                {linkSuffixText}
              </Typography>
            </Link>
          </Stack>
        </AccordionBadge>
      );
    }

    return (
      <AccordionBadge
        bgcolor={colorsByStatus[status].bgcolor}
        borderColor={colorsByStatus[status].borderColor}
      >
        <Typography variant={'body5'} tabIndex={0}>
          {statusDescription}
        </Typography>
      </AccordionBadge>
    );
  };

  const dataItems = [
    {
      label: dictionary.applicationNumber,
      content: getApplyNumber(),
      width: isDesktop ? '97px' : '126px',
    },
    {
      label: dictionary.statusUpdateDate,
      content: getStatusUpdateDate(),
      width: isDesktop ? '93px' : '126px',
    },
    {
      label: dictionary.status,
      content: getStatusContent(),
      width: isDesktop ? '221px' : '193px',
    },
  ];

  return (
    <Stack>
      <Stack direction={isDesktop ? 'row' : 'column'} alignItems={'flex-start'}>
        <Stack gap={isDesktop ? '14px' : '4px'} width={'164px'} flexShrink={0}>
          {type === APPLY_TYPES.telephone ? (
            <Stack direction={'row'} gap={'12px'}>
              <Typography variant={'body5'} tabIndex={0}>
                {dictionary.phoneApplication}
              </Typography>
              <Avatar
                alt={''}
                src={OutlinedPhone}
                sx={{ width: '20px', height: '20px' }}
              />
            </Stack>
          ) : (
            <Stack direction={'row'} gap={'12px'}>
              <Typography variant={'body5'} tabIndex={0}>
                {dictionary.onlineApplication}
              </Typography>
              <Avatar
                alt={''}
                src={OutlinedScreen}
                sx={{ width: '20px', height: '20px' }}
              />
            </Stack>
          )}
          {title ? (
            <Typography variant={'body1'} tabIndex={0}>
              {title}
            </Typography>
          ) : (
            <Typography variant={'body1'} tabIndex={0}>
              {dictionary.newRequest}
            </Typography>
          )}
        </Stack>
        <Divider
          orientation={isDesktop ? 'vertical' : 'horizontal'}
          aria-hidden={'true'}
          flexItem
          sx={{
            borderColor: 'primary.shade_100',
            margin: isDesktop ? '0 40px' : '12px 0',
          }}
        />
        <Stack
          direction={'row'}
          flexWrap={'wrap'}
          alignItems={'flex-start'}
          gap={isDesktop ? '56px' : '20px'}
        >
          {dataItems.map((dataItem, index) => (
            <DataItem
              width={dataItem.width}
              content={dataItem.content}
              label={dataItem.label}
              key={index}
            />
          ))}
        </Stack>
      </Stack>
      {isAdminIsrId && (
        <Stack
          alignItems={'center'}
          gap={'6px'}
          flexDirection={'row'}
          mt='20px'
        >
          <Avatar
            sx={{ width: '24px', height: '24px' }}
            alt={Dictionary[DICTIONARY.General].warning}
            src={WarningYellow}
          />
          <Typography variant='body3'>
            {Dictionary[DICTIONARY.Applies].openedByWing}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
